/** @file puppet.create: replaces the document's puppet with a new one-root puppet. */
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { compact } from '../validate/compact';
import { readFacingSteps } from '../validate/readFacingSteps';
import { readInput } from '../validate/readInput';
import { requireProfile } from '../validate/requireProfile';
import { newPuppet } from './newPuppet';

const positive = checkNumber({ above: 0 });

function readFacing(value: unknown, path: string): Puppet['facing'] {
  const r = readInput(value, path, ['steps', 'turnRate']);
  const turnRate = r.raw('turnRate');
  positive(turnRate, r.at('turnRate'));
  return { steps: readFacingSteps(r.raw('steps'), r.at('steps')), turnRate: turnRate as number };
}

/** `puppet.create { name, profile, facing? }` → `{ id }`; default facing 16 steps at 5 degrees per frame; inverse P. */
export const puppetCreateCommand: CommandDef<'puppet.create'> = {
  name: 'puppet.create',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['name', 'profile', 'facing']);
    return compact({ name: r.str('name'), profile: r.str('profile'), facing: r.opt('facing', (k) => readFacing(r.raw(k), r.at(k))) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const inverse = puppetInverse(draft);
    draft.puppet = newPuppet(ctx.makeId('pup'), input.name, requireProfile(input.profile, ctx), input.facing);
    return { result: { id: draft.puppet.id }, warnings: [], inverse };
  },
};
